// rrweb type aliases — rrweb 2.0.0-alpha.4 does not re-export these from its
// package root, so we define compatible local aliases to keep the same shape.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type eventWithTime = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type recordOptions<T = eventWithTime> = Record<string, any>;

// --- SDK Config ---

export interface NetworkSanitizeConfig {
  denyHeaders: string[];
  denyBodyUrls: string[];
}

export interface ClintplayConfig {
  projectId: string;
  apiUrl?: string;

  // Privacy
  maskAllInputs?: boolean;
  maskInputSelector?: string;
  blockSelector?: string;
  ignoreSelector?: string;
  networkSanitize?: NetworkSanitizeConfig;
  // Requests that are not recorded AT ALL — no network event and no error event
  // when they fail. Same glob syntax as `denyBodyUrls` (`*` = anything), matched
  // against the absolute URL. For other telemetry SDKs running on the page: their
  // export calls say nothing about the app, and the ones that fail on unload or
  // laptop sleep would show up as network errors. Your own `apiUrl` is always
  // skipped and does not need to be listed.
  networkIgnoreUrls?: string[];

  // Identity persistence for the anonymous id (cp_anon_id). 'memory' is a
  // genuine no-storage mode for consent-restrictive customers — unique-user
  // accuracy degrades to per-pageload (spec §5.1).
  persistence?: 'localStorage' | 'memory';

  // Feature toggles
  recordNetwork?: boolean;
  recordConsole?: boolean;
  trackPerformance?: boolean;
  trackRageClicks?: boolean;
  trackNavigation?: boolean;

  // Transport
  flushIntervalMs?: number;
  flushMaxBytes?: number;
  // Gzip event batches before sending. Cuts wire size 5-10x on rrweb-heavy
  // payloads. Falls back to uncompressed automatically on browsers without
  // CompressionStream (very old Safari, mostly). Default: true.
  compressEvents?: boolean;

  // rrweb passthrough
  rrwebConfig?: Partial<recordOptions<eventWithTime>>;
}

export const DEFAULT_CONFIG: Required<
  Pick<
    ClintplayConfig,
    | 'apiUrl'
    | 'maskAllInputs'
    | 'recordNetwork'
    | 'recordConsole'
    | 'trackPerformance'
    | 'trackRageClicks'
    | 'trackNavigation'
    | 'flushIntervalMs'
    | 'flushMaxBytes'
    | 'compressEvents'
    | 'persistence'
  >
> = {
  apiUrl: 'https://api.clintplay.example/v1',
  maskAllInputs: true,
  recordNetwork: true,
  recordConsole: true,
  trackPerformance: true,
  trackRageClicks: true,
  trackNavigation: true,
  compressEvents: true,
  persistence: 'localStorage',
  // Defaults tuned to keep request volume well below Datadog RUM levels on
  // a busy SPA. Customer-reported pain at 5s/50KB: rrweb fires 100s of
  // events per minute, threshold flushes hit every 1-2s, network panel
  // floods. 15s/256KB cuts request count ~10x with negligible impact on
  // capture latency. The visibility-change flush in transport.ts keeps
  // the tail data safe.
  flushIntervalMs: 15_000,
  flushMaxBytes: 256_000,
};

// --- Session ---

export interface Session {
  id: string;
  projectId: string;
  startedAt: number;
  userId?: string;
  userTraits?: Record<string, unknown>;
  tags: string[];
  metadata: Record<string, unknown>;
}

// --- Events ---

export type ClintplayEventType =
  | 'rrweb'
  | 'error'
  | 'network'
  | 'console'
  | 'performance'
  | 'rage-click'
  | 'breadcrumb'
  | 'identify'
  | 'tag'
  | 'metadata'
  | 'navigation'
  | 'track';

export interface ClintplayEvent {
  type: ClintplayEventType;
  timestamp: number;
  sessionId: string;
  data: unknown;
}

export interface ErrorEventData {
  message: string;
  stack?: string;
  source?: string;
  lineno?: number;
  colno?: number;
  type: 'onerror' | 'unhandledrejection';
  /**
   * Severity level of the error event.
   * - 'error': Fatal or unhandled runtime exceptions.
   * - 'warning': Benign diagnostic or layout warnings (e.g. ResizeObserver loop limit).
   */
  level?: 'error' | 'warning';
  /**
   * True if this event represents a benign layout warning rather than a fatal crash.
   */
  isLayoutWarning?: boolean;
}

export interface NetworkEventData {
  method: string;
  url: string;
  status?: number;
  statusText?: string;
  requestHeaders?: Record<string, string>;
  responseHeaders?: Record<string, string>;
  requestBody?: string;
  responseBody?: string;
  startTime: number;
  endTime?: number;
  duration?: number;
}

export interface ConsoleEventData {
  level: 'log' | 'warn' | 'error' | 'info' | 'debug';
  args: unknown[];
}

export interface PerformanceEventData {
  metric: 'LCP' | 'FID' | 'CLS' | 'TTFB' | 'INP';
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
}

export interface RageClickEventData {
  selector: string;
  clickCount: number;
  x: number;
  y: number;
}

export interface BreadcrumbEventData {
  category: 'navigation' | 'click' | 'input' | 'error' | 'network' | 'custom';
  message: string;
  data?: Record<string, unknown>;
}

export interface NavigationEventData {
  from: string | null;
  to: string;
  trigger: 'pushState' | 'replaceState' | 'popstate' | 'hashchange' | 'initial';
}

export interface TrackEventData {
  name: string;
  properties: Record<string, unknown>;
  /** Client-generated dedup key — retries of the same logical event carry
   *  the same uuid and collapse server-side. */
  eventUuid: string;
}

// --- Transport ---

export interface EventBatch {
  projectId: string;
  sessionId: string;
  /** Persistent anonymous id (cp_anon_id). Rides the ENVELOPE because the
   *  sendBeacon fallback cannot set headers. Absent in memory-less/legacy
   *  clients — the API accepts batches without it indefinitely. */
  anonId?: string;
  /** Version of the SDK build that produced this batch — the `version` field
   *  of packages/sdk/package.json, inlined by the bundler at build time (see
   *  `VERSION` in index.ts). Rides the ENVELOPE for the same reason `anonId`
   *  does: the sendBeacon fallback cannot set headers, and a new header would
   *  also need adding to the API's CORS allowedHeaders list.
   *
   *  ABSENT on every batch sent by an SDK older than this one, which is every
   *  batch recorded before this shipped. The API stores NULL for those and
   *  never infers a version from anything else — see the API's
   *  ingest/normalize-sdk-version.ts. */
  sdkVersion?: string;
  events: ClintplayEvent[];
  sentAt: number;
}
